import React, { Fragment, lazy, memo, useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { ReactTitle } from 'react-meta-tags';
import GA from 'react-ga';
import GA4 from 'react-ga4';
import NewspaperTile from '../../components/NewspaperTile';
import FilterButtons from '../../components/FilterButtons/FilterButtons';
import useSelectedNewspapers from '../../hooks/useSelectedNewspapers';
import { useNewspapersActionsDispatches } from '../../hooks/useNewspapers';
import Spinner from '../../components/Spinner';
import useAppGlobalData from '../../hooks/useAppGlobalData';
import routesPaths from '../../routes/routesPaths';
import { stringToMoment, throttle } from '../../shared/functions';
import NoData from '../../components/NoData';
import './newspaper-page.style.scss';
var SidebarAd = lazy(function () { return import('../../components/Ads/SidebarAdv'); });
var NewspaperGridAd = lazy(function () { return import('../../components/Ads/NewspaperGridAd'); });
var getPageTitle = function (title) {
    return title ? "\u038C\u03BB\u03B1 \u03C4\u03B1 \u03C0\u03C1\u03C9\u03C4\u03BF\u03C3\u03AD\u03BB\u03B9\u03B4\u03B1 \u03C4\u03B7\u03C2 " + title : 'Πρωτοσέλιδα Αθλητικών Εφημερίδων';
};
/**
 * A page that shows selected newspapers
 */
var NewspaperPage = function () {
    var _a = useNewspapersActionsDispatches(), fetchNewspapers = _a.fetchNewspapers, fetchNewspapersByMonthAndPublication = _a.fetchNewspapersByMonthAndPublication;
    var _b = useParams(), date = _b.date, publicationId = _b.publicationId, publicationSlug = _b.publicationSlug;
    var _c = useSelectedNewspapers(), loading = _c[0], newspapers = _c[1];
    var _d = useAppGlobalData(), selectedCriteria = _d.selectedCriteria, selectedNewsGroup = _d.selectedNewsGroup, selectedDate = _d.selectedDate, resetSearchCriteria = _d.resetSearchCriteria, onSelectedNewsGroupChange = _d.onSelectedNewsGroupChange;
    var fetchNewspapersMonthlyView = throttle(function (currentDate, id) {
        return fetchNewspapersByMonthAndPublication(currentDate, id);
    });
    var history = useHistory();
    var location = useLocation();
    var isApp = useMemo(function () {
        var query = new URLSearchParams(location.search);
        var appParam = query.get('sport24app');
        return appParam === 'true';
    }, [location]);
    // Fetch the newspapers for the given date in the url, if present
    useEffect(function () {
        if (date) {
            var fetchNewspapersDailyView = throttle(function () { return fetchNewspapers(stringToMoment(date)); });
            fetchNewspapersDailyView();
        }
    }, [date]);
    // When the component mounts, fetch the newspapers by publication if provided
    useEffect(function () {
        if (publicationId && selectedDate) {
            fetchNewspapersMonthlyView(stringToMoment(selectedDate), publicationId);
        }
    }, [publicationId, selectedDate]);
    // Resets search criteria and returns to main page with daily view (remembering selected date)
    var reset = useCallback(function () {
        resetSearchCriteria();
        if (!isApp) {
            history.push(routesPaths.newspaperPageBase + "/date/" + selectedDate);
        }
        else
            history.push(routesPaths.newspaperPageBase + "/date/" + selectedDate + "?sport24app=true");
    }, [history, resetSearchCriteria, routesPaths, isApp, selectedDate]);
    // Navigates to detail view of newspapers.
    // It makes use of route params or passed params, dependent on which params are available on the newspaper
    // (this can change between newspapers fetched for monthly view and for daily view)
    var toNewspaperDetail = useCallback(function (id, slug, datePublished) {
        if (!isApp) {
            history.push(routesPaths.newspaperPageBase + "/protoselido-" + (slug || publicationSlug) + "/" + (datePublished || date) + "/" + (id || publicationId));
        }
        else {
            history.push(routesPaths.newspaperPageBase + "/protoselido-" + (slug || publicationSlug) + "/" + (datePublished || date) + "/" + (id || publicationId) + "?sport24app=true");
        }
    }, [history, date, routesPaths, isApp, selectedDate]);
    /**
     * Google Analytics
     */
    useEffect(function () {
        /**
         * if we have a publicationId (from url params) we are waiting for selectedCriteria (publication title).
         * publicationId doesn't change for the same location, so if block fires again only after selectedCriteria fetches.
         * else block only fires once: if publicationId is empty, selectedCriteria never fetches.
         * date always triggers else block
         * (see routes to match url params to cases)
         */
        if (publicationId) {
            if (selectedCriteria) {
                GA.initialize('UA-482127-1');
                GA.ga('send', 'pageview', {
                    page: "" + location.pathname + location.search,
                    title: getPageTitle(selectedCriteria.title),
                });
            }
        }
        else {
            GA.initialize('UA-482127-1');
            GA.ga('send', 'pageview', {
                page: "" + location.pathname + location.search,
                title: getPageTitle(),
            });
        }
    }, [date, publicationId, selectedCriteria]);
    // selectedCriteria update is triggered by URL Params (in Header.tsx) so this only runs when location updates
    /**
     * Google Analytics 4
     */
    useEffect(function () {
        /**
         * if we have a publicationId (from url params) we are waiting for selectedCriteria (publication title).
         * publicationId doesn't change for the same location, so if block fires again only after selectedCriteria fetches.
         * else block only fires once: if publicationId is empty, selectedCriteria never fetches.
         * date always triggers else block
         * (see routes to match url params to cases)
         */
        if (publicationId) {
            if (selectedCriteria) {
                GA4.initialize([
                    {
                        trackingId: 'G-CBFPZXDS6N',
                    },
                    {
                        trackingId: 'G-NTXG7V0NM2',
                    },
                ]);
                GA4.ga('send', 'pageview', {
                    page: "" + location.pathname + location.search,
                    title: getPageTitle(selectedCriteria.title),
                });
            }
        }
        else {
            GA4.initialize([
                {
                    trackingId: 'G-CBFPZXDS6N',
                },
                {
                    trackingId: 'G-NTXG7V0NM2',
                },
            ]);
            GA4.ga('send', 'pageview', {
                page: "" + location.pathname + location.search,
                title: getPageTitle(),
            });
        }
    }, [date, publicationId, selectedCriteria]);
    // selectedCriteria update is triggered by URL Params (in Header.tsx) so this only runs when location updates
    return (React.createElement("div", { className: "tfm-newspaper-overview" },
        React.createElement(ReactTitle, { title: getPageTitle(selectedCriteria === null || selectedCriteria === void 0 ? void 0 : selectedCriteria.title) }),
        React.createElement("div", { className: "tfm-newspaper-page" },
            React.createElement("div", { className: "tfm-newspaper-page-content" },
                React.createElement(FilterButtons, { id: "articleFilterToggle", className: "tfm-newspaper-page-filter", theme: "light", showToggle: !selectedCriteria, onRoundBtnClicked: reset, onSwitch: onSelectedNewsGroupChange, currentSwitchValue: selectedNewsGroup }),
                loading && React.createElement(Spinner, { className: "tfm-newspaper-page-spinner" }),
                !loading &&
                    (newspapers.length !== 0 ? (React.createElement("div", { className: "tfm-newspaper-page-grid" }, newspapers.map(function (newspaper, i) { return (React.createElement(Fragment, { key: newspaper.id },
                        React.createElement(NewspaperTile, { index: i, className: "tfm-newspaper-page-grid-newspaper", key: newspaper.id, newspaper: newspaper, monthViewActive: !!selectedCriteria, onTileClick: toNewspaperDetail }),
                        (i + 1) % 6 === 0 && (React.createElement(NewspaperGridAd, { slotIndex: i, className: "tfm-newspaper-page-grid-ad" })))); }))) : (
                    // Tells user that no newspapers are available for this day and filter.
                    // Like in match center, is also shown when backend doesn't return data because there is no connection with server.
                    React.createElement(NoData, { text: "\u0394\u03B5\u03BD \u03C5\u03C0\u03AC\u03C1\u03C7\u03BF\u03C5\u03BD \u03B4\u03B5\u03B4\u03BF\u03BC\u03AD\u03BD\u03B1" })))),
            React.createElement(SidebarAd, { className: "tfm-newspaper-page-sidebar-ad" }))));
};
export default memo(NewspaperPage);
